import React, { ChangeEvent, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Select, { SingleValue } from 'react-select';
import { toast } from 'react-toastify';

import { RootState } from '@/store/rootReducer';
import { selectDefectDataForDivision } from '@/store/slices/masterDataSlice';
import { Images } from '../Images/Images';
import { FileDetails, Image } from '@/types/Image.type';
import { InspectionImageType } from '@/enums/InspectionImage.enum';
import { Footer, FooterProps } from '@/features/inspection-details/components/footer/footer';
import { imageStorage } from '@/store/idbStorage/imageStorage';

import styles from './DefectDetails.module.scss';
import { Mode } from '@/enums/Mode.enum';
import { DefectDetailsProps, SelectedDefectCode } from './DefectDetailsProps';
import { APP_CONFIG_KEY } from '@/types/AppConfig.type';

export const DefectDetails: React.FC<DefectDetailsProps> =
  ({ inspectionId, defect, actionHandlers, valueChangeHandlers, mode = Mode.ADD, divisionId = '' }: DefectDetailsProps) => {
    const { t: translate } = useTranslation()

    // Use the selector to get defect codes and severities for the specified division
    const { defectCodes, defectSeverities } = useSelector((state: RootState) =>
      selectDefectDataForDivision(state, divisionId)
    );
    const isDefectImageRequired = useSelector((state: RootState) => state.appConfig[APP_CONFIG_KEY.recordDefect]?.isDefectImageRequired);
    const isEditable = true;


    if (!defectCodes?.length) {
      toast.error(translate('Defect_Codes_Not_Available'));
      actionHandlers?.onCancel();
      return;
    }



    const sortedDefectCodes = [...defectCodes]
      .map(dc => ({
        ...dc,
        codes: [...dc.codes].sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
      }))
      .sort((a, b) => a.category.localeCompare(b.category));
    let selectedDefect: SelectedDefectCode = {
      category: sortedDefectCodes[0].category,
      defectCode: sortedDefectCodes[0].codes[0]
    };
    if (defect) {
      defectCodes.forEach((dc) => {
        const item = dc.codes.find(c => c.id === defect?.defectCode?.id);
        if (item) {
          selectedDefect = {
            category: dc.category,
            defectCode: item
          }
        }
      });
    }


    const codes = sortedDefectCodes.find(c => c.category === selectedDefect.category)?.codes || [];
    const defectCategoryOptions = sortedDefectCodes.map(c => ({
      value: c.category,
      label: `${c.category}: ${c.categoryDescription}`,
    }));

    const defectOptions = codes.map(c => ({
      value: c.id,
      label: `${c.code}: ${c.codeDescription}`,
    }));


    useEffect(() => {
      valueChangeHandlers?.onCodeChange(selectedDefect.defectCode);// set default values
    }, [defect?.id]);

    if (!inspectionId) {
      return "No indpection found"

    }
    const images = [...(defect?.existingImages || []), ...(defect?.newImages || [])].filter(i => !i.isDeleted);

    const handleQuantityChange = (event: ChangeEvent<HTMLInputElement>): void => {
      const castedValue = parseFloat(event.target.value);
      valueChangeHandlers?.onQuantityChange(castedValue);
    }

    const handleCommentsChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
      valueChangeHandlers?.onCommentsChange(event.target.value);
    }

    const handleCategoryChange = (event: SingleValue<{ value: string; label: string }>): void => {
      if (!event) return;
      const category = defectCodes.find(c => c.category === event?.value);
      if (!category) return;
      valueChangeHandlers?.onCodeChange([...category.codes].sort((a, b) => (a.index ?? 0) - (b.index ?? 0))[0]);

    }

    const handleCodeChange = (event: SingleValue<{ value: string; label: string }>): void => {
      if (!event) return;

      const selectedCode = codes.find(c => c.id === event.value);
      if (!selectedCode) return;
      valueChangeHandlers?.onCodeChange(selectedCode);
    }
    const handleSeverityChange = (event: ChangeEvent<HTMLInputElement>): void => {
      const severity = defectSeverities.find(s => s.id === event.target.value);
      if (!severity) return;

      valueChangeHandlers?.onSeverityChange(severity);
    }
    async function onAdd(file: FileDetails): Promise<void> {
      let img: Image = { url: '', type: InspectionImageType.DEFECT, isNew: true, ...file };
      try {
        if (img?.data) {
          await imageStorage.setImage(inspectionId, InspectionImageType.DEFECT, img.id, img.data);
        }
      } catch (error) {
        console.error('Failed to add image in IndexDB', error);
      }
      // img.data = '';
      // setImages(prev => [...prev, { ...img }]);
      valueChangeHandlers?.onImageAdd(img);
    }

    async function onRemove(img: Image): Promise<void> {
      // if (img.isNew) {
      //   await imageStorage.removeImage(inspectionId, InspectionImageType.DEFECT, img.id);
      // }
      // setImages(prev => prev.filter(i => i.id !== img.id));
      valueChangeHandlers?.onImageRemove(img);
    }
    const handleSaveClick = () => {
      if (isDefectImageRequired && (!images?.length)) {
        toast.error(translate('defect_image_required'));
        return;
      }
      if (!defect?.defectQuantity || defect?.defectQuantity <= 0) {
        toast.error(translate('defect_quantity_required'));
        return;
      }
      actionHandlers?.onSave();
      handleCloseClick();
    }
    const handleCloseClick = () => {
      actionHandlers?.onCancel();
    };
    const handleDeleteClick = () => {
      if (actionHandlers?.onDelete && defect) {
        actionHandlers?.onDelete(defect);
        handleCloseClick();
      }
    }
    const actionButtons: FooterProps = {
      secondaryButton: {
        label: 'Cancel',
        onClick: handleCloseClick
      },

    };
    if (isEditable) {
      actionButtons.primaryButton = {
        label: 'Save',
        onClick: handleSaveClick
      }
      if (mode === Mode.EDIT) {
        actionButtons.extraButton = {
          label: 'Delete',
          styleName: 'delete',
          onClick: handleDeleteClick
        }
      }
    }
    function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
      const input = e.currentTarget as HTMLInputElement;
      if (
        e.key === "." ||
        e.key === "-" ||
        e.key === "e" ||
        (e.key === "0" && input.value === "")
      ) {
        e.preventDefault();
      }
    }
    return (<>
      <div className={styles.formContainer}>
        <h2 className={styles.header}>{translate('Defect Details')}</h2>
        <div className={styles.inputGroup}>
          <label className={styles.label} htmlFor="defectCategory">{translate('Defect Category')}</label>
          <Select
            id="defectCategory"
            name="defectCategory"
            options={defectCategoryOptions}
            value={defectCategoryOptions.find(option => option.value === selectedDefect.category) || null}
            onChange={handleCategoryChange}
            placeholder={translate('Select or Search...')}
            isSearchable
            isDisabled={!isEditable}
            styles={{
              control: (provided) => ({
                ...provided,
                fontSize: '.85rem',
                color: '#000000B3',
                borderColor: "#A7A7A7",
                boxShadow: 'none',
                "&:hover": { borderColor: '#A7A7A7' },
              }),
              option: (provided, state) => ({
                ...provided,
                fontSize: '.85rem',
                backgroundColor: state.isSelected ? "#0C63E4" : state.isFocused ? "#0C63E4" : "white",
                color: state.isSelected || state.isFocused ? "white" : "#000000B3",
              }),
            }}
          />

        </div>
        <div className={styles.inputGroup}>
          <label className={styles.label} htmlFor="defectCode">{translate('Defect Type')}</label>
          <Select

            id="defectCode"
            name="defectCode"
            options={defectOptions}
            value={defectOptions.find(option => option.value === selectedDefect?.defectCode?.id) || null}
            onChange={handleCodeChange}
            placeholder={translate('Select or Search...')}
            isSearchable
            isDisabled={!isEditable}
            styles={{
              control: (provided) => ({
                ...provided,
                fontSize: '.85rem',
                color: '#000000B3',
                borderColor: "#A7A7A7",
                boxShadow: 'none',
                "&:hover": { borderColor: '#A7A7A7' },
              }),
              option: (provided, state) => ({
                ...provided,
                fontSize: '.85rem',
                backgroundColor: state.isSelected ? "#0C63E4" : state.isFocused ? "#0C63E4" : "white",
                color: state.isSelected || state.isFocused ? "white" : "#000000B3",
              }),
            }}
          />
        </div>
        <div className={styles.inputGroup}>
          <label className={styles.label}>{translate('Defect Severity')}</label>
          <div className={styles.radioGroup}>
            {defectSeverities.map(s =>
              <label className={styles.labels} key={s.id}>
                <input type="radio" disabled={!isEditable} name="defectSeverity" value={s.id} checked={defect?.defectSeverityId === s.id} onChange={handleSeverityChange} />
                {translate(s.name)}
              </label>)}
          </div>
        </div>
        <div className={styles.inputDefectQty}>
          <label className={styles.label} htmlFor="defectQuantities">{translate('Defect Quantities')}</label>
          <input disabled={!isEditable} onKeyDown={onKeyDown} min='1' type="number" id="defectQuantities"
            name="defectQuantities" value={defect?.defectQuantity ?? 1} onChange={handleQuantityChange} />
        </div>
        <div className={styles.inputGroup}>
          <label className={styles.label} htmlFor="comments">{translate('Comments')}</label>
          <textarea id="comments" disabled={!isEditable} rows={2} name="comments" value={defect?.comment} onChange={handleCommentsChange}></textarea>
        </div>
      </div>
      <Images
        type={InspectionImageType.DEFECT}
        images={images}
        onAdd={onAdd}
        onRemove={onRemove}
        inspectionId={inspectionId}
        gallerySectionLabel={
          defect?.defectCode?.codeDescription?.trim()
            ? defect.defectCode.codeDescription.trim()
            : translate('defect_images')
        }
      />
      <Footer {...actionButtons} />
    </>
    );
  }